import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, LayersControl, GeoJSON, Marker, Tooltip, LayerGroup } from "react-leaflet";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import jsPDF from 'jspdf';
import domtoimage from 'dom-to-image-more';
import autoTable from "jspdf-autotable";
import "./Advisory.css";
import { MapHomeControl, MapInfoControl, MapLegendControl } from "../../../common/MapControls/MapControls";
import {
    drawReportHeader, drawReportFooter, drawEventSummaryCard, COLORS, HEADER_H, FOOTER_H,
} from "../../../common/reportTemplate/reportTemplate.js";

const MapWithGeoJSON = () => {
    const URLTILEMAP_WHITE = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
    const [stations, setStations] = useState([]);
    const [riverData, setRiverData] = useState(null);
    const [DilliCatchments, setDilliCatchments] = useState(null);
    const [boundaryData, setBoundaryData] = useState(null);
    const [stationForecasts, setStationForecasts] = useState({});
    const [catchmentRainfalls, setCatchmentRainfalls] = useState({});
    const mapRef = useRef(); // 👈 Reference to map
    const mapWrapperRef = useRef(null); // use this for image capture
    const [showLanguageModal, setShowLanguageModal] = useState(false);
    const [, setSelectedLanguage] = useState(null);
    const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;
    const defaultDays = 6;
    const selectedDateStations = new Date().toISOString().slice(0, 10);
    const selectedDateCatchments = new Date().toISOString().slice(0, 10);

    const stationIcon = L.icon({
        iconUrl: "/DATA/Flood/outlet.png", // replace filename as needed
        iconSize: [15, 15],
        iconAnchor: [7, 7]
    });

    useEffect(() => {
        fetch("/DATA/Flood/dili_river.geojson")
            .then((res) => res.json())
            .then(setRiverData)
            .catch(err => console.error("Boundary load error:", err));
    }, []);

    useEffect(() => {
        fetch("/DATA/Flood/dili_catchments.geojson")
            .then((res) => res.json())
            .then(setDilliCatchments)
            .catch(err => console.error("Boundary load error:", err));
    }, []);


    useEffect(() => {
        fetch(`${backendUrl}/flood/hydrological-stations/`)
            .then((res) => res.json())
            .then((data) => {
                if (Array.isArray(data)) {
                    setStations(data);
                }
            })
            .catch(err => console.error("Stations load error:", err));
    }, [backendUrl]);
    // Prefetch 7-day discharge for a few stations to include in PDF
    useEffect(() => {
        if (!stations || stations.length === 0 || !backendUrl) return;
        const limited = stations.slice(0, 5);
        Promise.all(
            limited.map((s) => {
                const url = `${backendUrl}/flood/hydrological-stations-discharge-data/?station_id=${encodeURIComponent(s.id)}&dt=${encodeURIComponent(selectedDateStations)}&days=${defaultDays}`;
                return fetch(url)
                    .then((res) => res.json())
                    .then((data) => ({ id: s.id, name: s.station_name, data }))
                    .catch(() => ({ id: s.id, name: s.station_name, data: [] }));
            })
        ).then((results) => {
            const next = {};
            results.forEach((r) => {
                let points = [];
                if (Array.isArray(r.data)) {
                    points = r.data
                        .filter((row) => row && row.dt && (row.discharge != null || row.flow_median != null))
                        .map((row) => ({ dt: row.dt, discharge: Number(row.discharge ?? row.flow_median) }));
                } else if (r.data && typeof r.data === 'object') {
                    const datetimes = r.data.dt || r.data.datetimes || [];
                    const discharge = r.data.discharge || r.data.flow_median || [];
                    if (datetimes.length && discharge.length) {
                        points = datetimes.map((dt, i) => ({ dt, discharge: discharge[i] }));
                    }
                }
                next[r.id] = { name: r.name, points };
            });
            setStationForecasts(next);
        });
    }, [stations, backendUrl, selectedDateStations]);

    // Load the GeoJSON boundary data
    useEffect(() => {
        fetch("/DATA/OSMbased_adm2_boundary.json")
            .then((response) => response.json())
            .then((data) => setBoundaryData(data))
            .catch((error) => console.error("Error loading boundary data:", error));
    }, []);
    // Prefetch 7-day rainfall for a few catchments to include in PDF
    useEffect(() => {
        if (!DilliCatchments || !DilliCatchments.features || !backendUrl) return;
        let feats = DilliCatchments.features;
        const preferred = feats.filter((f) => String(f.properties?.Catchment_ || '').toLowerCase().includes('comoro'));
        if (preferred.length) {
            feats = preferred.concat(feats.filter((f) => preferred.indexOf(f) === -1));
        }
        const limited = feats.slice(0, 5);
        Promise.all(
            limited.map((f) => {
                const cid = f.properties?.OBJECTID_1 ?? f.properties?.OBJECTID ?? f.properties?.id;
                const name = f.properties?.Catchment_ || f.properties?.name || `Catchment ${cid}`;
                if (cid == null || cid === '' || isNaN(Number(cid))) {
                    return Promise.resolve({ cid, name, data: [] });
                }
                const url = `${backendUrl}/flood/catchment-precipitation-data/?catchment_id=${encodeURIComponent(cid)}&dt=${encodeURIComponent(selectedDateCatchments)}&days=${defaultDays}`;
                return fetch(url)
                    .then((res) => res.json())
                    .then((data) => ({ cid, name, data }))
                    .catch(() => ({ cid, name, data: [] }));
            })
        ).then((results) => {
            const next = {};
            results.forEach((r) => {
                let points = [];
                if (Array.isArray(r.data)) {
                    points = r.data
                        .filter((row) => row && row.dt && (row.precipitation != null))
                        .map((row) => ({ dt: row.dt, rainfall: Number(row.precipitation) }));
                } else if (r.data && typeof r.data === 'object') {
                    const datetimes = r.data.dt || [];
                    const precip = r.data.precipitation || [];
                    if (datetimes.length && precip.length) {
                        points = datetimes.map((dt, i) => ({ dt, rainfall: precip[i] }));
                    }
                }
                next[r.cid] = { name: r.name, points };
            });
            setCatchmentRainfalls(next);
        });
    }, [DilliCatchments, backendUrl, selectedDateCatchments]);

    useEffect(() => {
        if (mapRef.current && boundaryData) {
            const layer = L.geoJSON(boundaryData);
            mapRef.current.fitBounds(layer.getBounds());
        }
    }, [boundaryData]);

    const riverStyle = {
        color: '#0099FF',
        weight: 1,
    };

    const loadImage = (src) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });
    };
    const captureAndUploadImage = (language) => {
        const button = document.getElementById('generate-advisory-button');
        if (!mapWrapperRef.current) return;
        // Temporarily hide the button
        button.style.display = 'none';

        domtoimage.toPng(mapWrapperRef.current)
            .then(async (dataUrl) => {
                // Show the button again after capture
                button.style.display = 'block';
                generateSimplePdf(dataUrl, language);
            })
            .catch((error) => {
                console.error("Failed to capture image:", error);
                button.style.display = 'block';
            });
    };

    function safeFormatTimorDate(date) {
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            console.error('Invalid date passed for formatting:', date);
            return 'Invalid Date';
        }
        const parts = new Intl.DateTimeFormat("en-GB", {
            timeZone: "Asia/Dili",
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        }).formatToParts(date);

        const getPart = (type) => parts.find(p => p.type === type)?.value || '';

        return `${getPart('day')} ${getPart('month')} ${getPart('year')} ${getPart('hour')}:${getPart('minute')}:${getPart('second')} ${getPart('dayPeriod').toUpperCase()} East Timor Time (TLT)`;
    }

    const generateSimplePdf = async (dataUrl, language = 'en') => {
        try {
            const pdf = new jsPDF();
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const leftMargin = 10;
            const rightMargin = 10;
            const textMaxWidth = pageWidth - leftMargin - rightMargin;

            const floodHeaderOpts = {
                title: "TIMOR LESTE URBAN FLOOD ADVISORY",
                issuedBy: "DNMG-APC",
                issuedAt: safeFormatTimorDate(new Date()),
            };
            await drawReportHeader(pdf, floodHeaderOpts);
            const headerHeight = HEADER_H + 5;
            const resetCurrentY = () => headerHeight + 5;
            let currentY = headerHeight + 3;

            const advisoryNo = "01/2025";

            // Event summary card — replaces the old free-text "Overview"
            // paragraph with the branded card used across every advisory.
            currentY = drawEventSummaryCard(pdf, currentY, {
                heading: "URBAN FLOOD ADVISORY",
                detailLines: [
                    `Advisory No: ${advisoryNo}`,
                    "Issued by: Department of Meteorology (DNMG)",
                    "Elevated flood risk from intense, prolonged rainfall — ground saturation and slope conditions raise urban flood risk in several areas.",
                ],
                badgeText: "FLOOD",
                badgeColor: COLORS.footerBlue,
                sideLabel: "STATUS",
                sideValue: "ACTIVE",
                sideValueColor: COLORS.riskHigh,
                height: 26,
            });
            currentY += 6;

            // Map title — section pills recoloured to the report's navy/white,
            // in place of the old generic pale-blue bars.
            pdf.setFillColor(0, 74, 173);
            pdf.roundedRect(leftMargin, currentY, textMaxWidth, 8, 1, 1, 'F');
            pdf.setFont("helvetica", "bold").setFontSize(11).setTextColor(255, 255, 255);
            pdf.text("Vulnerable Map", leftMargin + 2, currentY + 6);
            currentY += 12;

            const img = await loadImage(dataUrl);
            const aspectRatio = img.width / img.height;
            const imgWidth = 190;
            const imgHeight = imgWidth / aspectRatio;

            // Map image
            pdf.setTextColor(0, 0, 0);
            pdf.addImage(dataUrl, "PNG", 10, currentY, imgWidth, imgHeight);
            currentY += imgHeight + 10;

            // Small note under map instead of dummy table
            pdf.setFontSize(9);
            pdf.text("Map shows Dili River Network, administrative boundary and station locations.", leftMargin, currentY);
            currentY += 15;
            pdf.addPage();
            currentY = resetCurrentY();
            // Sub-Basin Forecast
            pdf.setFillColor(0, 74, 173);
            pdf.roundedRect(leftMargin, currentY, textMaxWidth, 8, 1, 1, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFont("helvetica", "bold").setFontSize(11);
            pdf.text("Station Wise Discharge(m\u00b3/s) Forecast (7 days)", leftMargin + 2, currentY + 6);
            currentY += 16;
            // Real station aggregated table (Date + one column per station)
            const stationIds = Object.keys(stationForecasts || {});
            if (stationIds.length === 0) {
                pdf.setFontSize(10);
                pdf.text("No station forecast data available.", leftMargin, currentY);
                currentY += 10;
            } else {
                // Build header: Date + station names
                const stationOrder = stationIds; // keep existing order
                const headRow = ["Date"].concat(stationOrder.map((sid) => (stationForecasts[sid]?.name || sid)));
                // Build date list from first station (7 days expected)
                const firstPoints = stationForecasts[stationOrder[0]]?.points || [];
                const dateKeys = firstPoints.map((p) => p.dt);
                const body = dateKeys.map((dt) => {
                    const row = [dt];
                    stationOrder.forEach((sid) => {
                        const entry = (stationForecasts[sid]?.points || []).find((p) => p.dt === dt);
                        const val = entry && typeof entry.discharge === 'number' ? entry.discharge.toFixed(2) : (entry ? entry.discharge : '');
                        row.push(val);
                    });
                    return row;
                });
                autoTable(pdf, {
                    startY: currentY,
                    head: [headRow],
                    body: body,
                    margin: { top: resetCurrentY(), left: leftMargin, right: rightMargin },
                    styles: { fontSize: 9, halign: 'center' },
                    headStyles: { fillColor: [0, 74, 173], textColor: [255, 255, 255], fontStyle: "bold" },
                    didDrawPage: (data) => {
                        data.settings.margin.top = resetCurrentY();
                    }
                });
                currentY = pdf.lastAutoTable.finalY + 10;
            }
            // Catchment Rainfall (7 days)
            pdf.setFillColor(0, 74, 173);
            pdf.roundedRect(leftMargin, currentY, textMaxWidth, 8, 1, 1, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFont("helvetica", "bold").setFontSize(11);
            pdf.text("Catchment Precipitation Rainfall(mm) (7 days)", leftMargin + 2, currentY + 6);
            currentY += 12;
            const catchmentIds = Object.keys(catchmentRainfalls || {});
            if (catchmentIds.length === 0) {
                pdf.setFontSize(10);
                pdf.text("No catchment rainfall data available.", leftMargin, currentY);
                currentY += 10;
            } else {
                const catchmentOrder = catchmentIds;
                const headRow = ["Date"].concat(catchmentOrder.map((cid) => (catchmentRainfalls[cid]?.name || cid)));
                const firstPointsC = catchmentRainfalls[catchmentOrder[0]]?.points || [];
                const dateKeysC = firstPointsC.map((p) => p.dt);
                const body = dateKeysC.map((dt) => {
                    const row = [dt];
                    catchmentOrder.forEach((cid) => {
                        const entry = (catchmentRainfalls[cid]?.points || []).find((p) => p.dt === dt);
                        const val = entry && typeof entry.rainfall === 'number' ? entry.rainfall.toFixed(2) : (entry ? entry.rainfall : '');
                        row.push(val);
                    });
                    return row;
                });
                autoTable(pdf, {
                    startY: currentY,
                    head: [headRow],
                    body: body,
                    margin: { top: resetCurrentY(), left: leftMargin, right: rightMargin },
                    styles: { fontSize: 9, halign: 'center' },
                    headStyles: { fillColor: [0, 74, 173], textColor: [255, 255, 255], fontStyle: "bold" },
                    didDrawPage: (data) => {
                        data.settings.margin.top = resetCurrentY();
                    }
                });
                currentY = pdf.lastAutoTable.finalY + 10;
            }

            // Note
            if (currentY + 20 > pageHeight) {
                pdf.addPage();
                currentY = resetCurrentY();
                //currentY = 20;
            }

            const noteText = `Note: This report is valid for next 24 hours. Please monitor the website for additional information and alerts issued by the Department of Meteorology.`;
            pdf.setFontSize(9);
            pdf.setTextColor(0, 0, 0);
            pdf.setFont("helvetica", "normal");
            const wrappedNote = pdf.splitTextToSize(noteText, textMaxWidth);
            // Check if note fits in remaining space, else add new page
            const estimatedNoteHeight = wrappedNote.length * 4.5;
            if (currentY + estimatedNoteHeight > pageHeight - 20) {
                pdf.addPage();
                currentY = resetCurrentY();
            }
            pdf.text(wrappedNote, leftMargin, currentY);
            // Footer + branded header on every page
            const pageCount = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                pdf.setPage(i);
                if (i > 1) await drawReportHeader(pdf, floodHeaderOpts);
                drawReportFooter(pdf, { message: "This advisory is valid for the next 24 hours." });
                pdf.setFontSize(7);
                pdf.setTextColor(120, 120, 120);
                pdf.text(`Page ${i} of ${pageCount}`, pageWidth - rightMargin, pageHeight - FOOTER_H - 6, { align: "right" });
            }

            currentY += 15;

            pdf.setFont('helvetica', 'bold');
            pdf.text('***', (pageWidth - pdf.getTextWidth('*****')) / 2, currentY);
            currentY += 6;
            pdf.setFont('helvetica', 'normal');

            pdf.save("DRRDSS-Flood-Risk-Advisory.pdf");
        } catch (error) {
            console.error("Error generating simple PDF:", error);
        }
    };

    const [selCatchment, setSelCatchment] = useState(null);
    const CatchmentFeature = (feature, layer) => {
        layer.on({
            click: () => {
                setSelCatchment(`
                    <div>
                        <strong>Catchment</strong>
                        <p>Name: ${feature.properties.Catchment_}</p>
                    </div>
                `);
            }
        });
    };

    const handleLanguageSelection = (lang) => {
        setSelectedLanguage(lang);
        setShowLanguageModal(false);
        captureAndUploadImage(lang); // Pass language to the generator function
    };

    return (
        <>
        {/* Generate Advisory Button - above map */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
            <button
                id="generate-advisory-button"
                onClick={() => setShowLanguageModal(true)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 16px",
                    backgroundColor: "#0d6efd",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 2px 6px rgba(13, 110, 253, 0.3)",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                }}
            >
                📄 Generate Advisory
            </button>
        </div>
        <div ref={mapWrapperRef} style={{ position: 'relative', borderRadius: "12px", overflow: "hidden" }}>
            <MapContainer center={[-8.6450, 125.6877]} zoom={11} style={{ width: "100%", height: "calc(95vh - 120px)" }} zoomControl={false} attributionControl={false} >
              <MapHomeControl />
              <MapInfoControl text="Flood advisory map. Click a basin or station to view the latest advisory details." />

                <TileLayer
                    url={URLTILEMAP_WHITE}
                />
                <LayersControl position="bottomright">


                    <LayersControl.Overlay name="Timor-Leste"  >
                        {boundaryData && (
                            <GeoJSON
                                data={boundaryData}
                                style={{
                                    color: "#ff0000",
                                    weight: 1,
                                    fillOpacity: 0,
                                    zIndex: -1999,
                                    interactive: false // <- important
                                }}
                            />
                        )}
                    </LayersControl.Overlay>
                    {/* Render the boundary GeoJSON layer if data is loaded */}
                    {selCatchment && (
                        <div
                            className="hover-box-hyd-forecast"
                            style={{ top: '10px', left: '10px' }}
                            dangerouslySetInnerHTML={{ __html: selCatchment }}
                        />
                    )}
                    <LayersControl.Overlay name="Dili Catchment" checked>
                        {DilliCatchments && <GeoJSON data={DilliCatchments} interactive={true} style={{ color: "#125ca6ff", weight: 1, fillOpacity: 0.1 }} onEachFeature={CatchmentFeature} />}
                    </LayersControl.Overlay>
                    <LayersControl.Overlay name="Dili River Network" checked>
                        {riverData && (
                            <GeoJSON
                                data={riverData}
                                style={riverStyle}
                            />
                        )}
                    </LayersControl.Overlay>

                    <LayersControl.Overlay name="Stations" checked>
                        <LayerGroup>
                            {stations && stations.map((s) => (
                                <Marker
                                    key={s.id}
                                    position={[s.latitude, s.longitude]}
                                    icon={stationIcon}
                                >
                                    <Tooltip direction="right" offset={[8, 0]} opacity={1} permanent={false}>
                                        {s.station_name}
                                    </Tooltip>
                                </Marker>
                            ))}
                        </LayerGroup>
                    </LayersControl.Overlay>
                </LayersControl>
                {/* Legend */}
                <MapLegendControl
                  sections={[{
                    shape: "square",
                    items: [
                      { label: "Dili River Network", icon: <span style={{ display: "inline-block", width: "18px", height: "3px", borderRadius: "2px", backgroundColor: "#0099FF" }} /> },
                      { label: "Timor-Leste Boundary", color: "#ff0000" },
                      { label: "Dili Catchment", color: "#125ca6" },
                      { label: "Station", icon: <img src="/DATA/Flood/outlet.png" alt="Station" style={{ width: "18px", height: "18px" }} /> },
                    ],
                  }]}
                />
                {/* Button moved outside map */}
            </MapContainer>
            {showLanguageModal && (
                <div
                    onClick={() => setShowLanguageModal(false)}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(0, 0, 0, 0.45)",
                        backdropFilter: "blur(4px)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: "#ffffff",
                            borderRadius: "16px",
                            padding: "32px 36px",
                            minWidth: "360px",
                            maxWidth: "420px",
                            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
                            textAlign: "center",
                        }}
                    >
                        {/* Icon */}
                        <div style={{
                            width: "52px",
                            height: "52px",
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, #e8f0fe, #d4e4fd)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 16px",
                            fontSize: "22px",
                        }}>
                            🌐
                        </div>

                        <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: 700, color: "#1a1a2e" }}>
                            Select Report Language
                        </h3>
                        <p style={{ margin: "0 0 24px", fontSize: "13px", color: "#6b7280", lineHeight: 1.5 }}>
                            Choose the language for the generated advisory PDF report.
                        </p>

                        <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                            {/* English Card */}
                            <div
                                onClick={() => handleLanguageSelection("en")}
                                style={{
                                    flex: 1,
                                    padding: "18px 12px",
                                    borderRadius: "12px",
                                    border: "1.5px solid #e2e8f0",
                                    backgroundColor: "#fafbfc",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "8px",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = "#0d6efd";
                                    e.currentTarget.style.backgroundColor = "#f0f6ff";
                                    e.currentTarget.style.transform = "translateY(-2px)";
                                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(13, 110, 253, 0.15)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = "#e2e8f0";
                                    e.currentTarget.style.backgroundColor = "#fafbfc";
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "none";
                                }}
                            >
                                <span style={{ fontSize: "28px" }}>🇬🇧</span>
                                <span style={{ fontSize: "14px", fontWeight: 600, color: "#1a1a2e" }}>English</span>
                            </div>

                            {/* Tetum Card */}
                            <div
                                onClick={() => handleLanguageSelection("tet")}
                                style={{
                                    flex: 1,
                                    padding: "18px 12px",
                                    borderRadius: "12px",
                                    border: "1.5px solid #e2e8f0",
                                    backgroundColor: "#fafbfc",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "8px",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = "#28a745";
                                    e.currentTarget.style.backgroundColor = "#f0faf3";
                                    e.currentTarget.style.transform = "translateY(-2px)";
                                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(40, 167, 69, 0.15)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = "#e2e8f0";
                                    e.currentTarget.style.backgroundColor = "#fafbfc";
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "none";
                                }}
                            >
                                <span style={{ fontSize: "28px" }}>🇹🇱</span>
                                <span style={{ fontSize: "14px", fontWeight: 600, color: "#1a1a2e" }}>Tetum</span>
                            </div>
                        </div>

                        {/* Cancel */}
                        <button
                            onClick={() => setShowLanguageModal(false)}
                            style={{
                                marginTop: "20px",
                                padding: "8px 24px",
                                backgroundColor: "transparent",
                                color: "#6b7280",
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: 500,
                                transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#f3f4f6";
                                e.currentTarget.style.borderColor = "#d1d5db";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.borderColor = "#e2e8f0";
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
        </>
    );
};
export default MapWithGeoJSON;